<script setup lang="ts">
/**
 * Per-response thumbs up/down + optional comment
 * (tutor-uox.1 + tutor-uox.1.1).
 *
 * Renders below a tutor reply (generated question, grade, chat reply).
 * The thumb buttons submit immediately; clicking either reveals a
 * comment box that's optional and submits on its own button.
 * Re-clicking the same thumb keeps it selected (no-op); clicking the
 * other thumb overwrites the rating server-side
 * (POST /feedback/response is idempotent on user+target).
 *
 * Two target shapes (mutually exclusive):
 *  - interactionId — practice / mock surfaces, where the rated reply
 *    is an Interaction row.
 *  - sessionId + turnIndex — tutor chat surface, where the rated reply
 *    is the assistant message at turn_index in the session's
 *    message_history JSON.
 *
 * Comments are free text and may contain incidental PII — never
 * mirror to error logs or analytics. The Statsig event (tutor-70k.7)
 * carries only the rating, not the comment text.
 */

import { computed, ref } from "vue";

import { apiClient } from "@/api/client";
import type { ResponseFeedbackTarget, ResponseRating } from "@/api/types";

const props = defineProps<{
  interactionId?: string;
  sessionId?: string;
  turnIndex?: number;
}>();

const target = computed<ResponseFeedbackTarget>(() => {
  if (props.interactionId !== undefined) {
    return { interaction_id: props.interactionId };
  }
  if (props.sessionId !== undefined && props.turnIndex !== undefined) {
    return { tutor_session_id: props.sessionId, turn_index: props.turnIndex };
  }
  throw new Error(
    "ResponseFeedback requires either interactionId, or both sessionId and turnIndex.",
  );
});

// Stable string key for DOM ids that's unique per (target). Falls back
// to interactionId for the existing practice/mock mounts so any tests
// asserting on `fb-comment-${interactionId}` keep working.
const widgetKey = computed(
  () => props.interactionId ?? `${props.sessionId}-${props.turnIndex}`,
);

const COMMENT_MAX = 500;

const rating = ref<ResponseRating | null>(null);
const comment = ref("");
const expanded = ref(false);
const submitting = ref(false);
const error = ref<string | null>(null);
const commentSavedAt = ref<number | null>(null);

const charsLeft = computed(() => COMMENT_MAX - comment.value.length);
const commentTooLong = computed(() => comment.value.length > COMMENT_MAX);

async function pick(next: ResponseRating): Promise<void> {
  if (submitting.value) return;
  if (rating.value === next) {
    expanded.value = !expanded.value;
    return;
  }
  const previous = rating.value;
  rating.value = next;
  expanded.value = true;
  error.value = null;
  submitting.value = true;
  try {
    await apiClient.submitResponseFeedback(
      target.value,
      next,
      comment.value.trim() || null,
    );
  } catch {
    rating.value = previous;
    error.value = "Could not save your feedback. Please try again.";
  } finally {
    submitting.value = false;
  }
}

async function saveComment(): Promise<void> {
  if (rating.value === null || submitting.value || commentTooLong.value) return;
  error.value = null;
  submitting.value = true;
  try {
    await apiClient.submitResponseFeedback(
      target.value,
      rating.value,
      comment.value.trim() || null,
    );
    commentSavedAt.value = Date.now();
  } catch {
    error.value = "Could not save your comment. Please try again.";
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="response-feedback" data-testid="response-feedback">
    <div class="row">
      <span class="prompt">Was this helpful?</span>
      <button
        type="button"
        class="thumb"
        :class="{ active: rating === 'up' }"
        :aria-pressed="rating === 'up'"
        :disabled="submitting"
        data-testid="thumbs-up"
        aria-label="Thumbs up"
        @click="pick('up')"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M2 21h4V9H2v12Zm20-11a2 2 0 0 0-2-2h-6.31l.95-4.57.03-.32a1.5 1.5 0 0 0-.44-1.06L13.17 1 6.59 7.59A2 2 0 0 0 6 9v10a2 2 0 0 0 2 2h9a2 2 0 0 0 1.84-1.22l3.02-7.05a2 2 0 0 0 .14-.73V10Z"
            fill="currentColor"
          />
        </svg>
      </button>
      <button
        type="button"
        class="thumb"
        :class="{ active: rating === 'down' }"
        :aria-pressed="rating === 'down'"
        :disabled="submitting"
        data-testid="thumbs-down"
        aria-label="Thumbs down"
        @click="pick('down')"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M22 3h-4v12h4V3Zm-9.16 17.06.07.31a1.5 1.5 0 0 0 .44 1.06L14.17 22l6.59-6.59A2 2 0 0 0 17 14V4a2 2 0 0 0-2-2H6a2 2 0 0 0-1.84 1.22L1.14 10.27A2 2 0 0 0 1 11v2a2 2 0 0 0 2 2h6.31l-.95 4.57Z"
            fill="currentColor"
          />
        </svg>
      </button>
    </div>
    <div v-if="expanded && rating !== null" class="comment-box">
      <label :for="`fb-comment-${widgetKey}`" class="visually-hidden">
        Optional comment
      </label>
      <textarea
        :id="`fb-comment-${widgetKey}`"
        v-model="comment"
        rows="2"
        :placeholder="
          rating === 'down'
            ? 'What went wrong? (optional)'
            : 'Anything to add? (optional)'
        "
        :maxlength="COMMENT_MAX"
        data-testid="feedback-comment"
      />
      <div class="comment-actions">
        <span class="counter" :class="{ over: commentTooLong }">
          {{ charsLeft }}
        </span>
        <button
          type="button"
          class="primary"
          :disabled="submitting || commentTooLong || comment.trim() === ''"
          data-testid="feedback-submit-comment"
          @click="saveComment"
        >
          {{ submitting ? "Saving…" : "Send" }}
        </button>
      </div>
    </div>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <p
      v-else-if="commentSavedAt && !submitting"
      class="muted"
      data-testid="feedback-saved"
    >
      Thanks — feedback saved.
    </p>
  </div>
</template>

<style scoped>
.response-feedback {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.prompt {
  font-size: 0.875rem;
  color: var(--text-muted, #6b6b6b);
}

.thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid var(--border-subtle, #d8d2c2);
  background: transparent;
  border-radius: 0.375rem;
  color: var(--text-muted, #6b6b6b);
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}

.thumb:hover:not(:disabled) {
  background: var(--surface-2, #f5efe1);
  color: var(--text, #2b2b2b);
}

.thumb.active {
  background: var(--accent, #2b2b2b);
  color: var(--surface, #faf6ec);
  border-color: var(--accent, #2b2b2b);
}

.thumb:disabled {
  opacity: 0.5;
  cursor: default;
}

.comment-box {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.comment-box textarea {
  width: 100%;
  padding: 0.5rem;
  font: inherit;
  border: 1px solid var(--border-subtle, #d8d2c2);
  border-radius: 0.375rem;
  resize: vertical;
}

.comment-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}

.counter {
  font-size: 0.75rem;
  color: var(--text-muted, #6b6b6b);
}

.counter.over {
  color: var(--danger, #b00020);
}

.primary {
  padding: 0.375rem 0.875rem;
  border: 1px solid var(--accent, #2b2b2b);
  background: var(--accent, #2b2b2b);
  color: var(--surface, #faf6ec);
  border-radius: 0.375rem;
  cursor: pointer;
}

.primary:disabled {
  opacity: 0.5;
  cursor: default;
}

.error {
  font-size: 0.875rem;
  color: var(--danger, #b00020);
  margin: 0;
}

.muted {
  font-size: 0.875rem;
  color: var(--text-muted, #6b6b6b);
  margin: 0;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
